import React from 'react';
import { Activity, Battery, FileCode2, FileText, Radio, ShieldAlert, ShieldCheck } from 'lucide-react';

export type DeviceStatPill = {
    label: string;
    value: string;
    icon: 'firmware' | 'battery' | 'race' | 'debug' | 'heard' | 'reason' | 'provisioning';
    /** De-emphasize metrics that do not apply at the current race stage. */
    muted?: boolean;
    title?: string;
};

function iconFor(kind: DeviceStatPill['icon'], muted: boolean): React.JSX.Element {
    const tone = muted ? 'text-gray-400 dark:text-slate-600' : '';
    switch (kind) {
        case 'firmware':
            return <FileCode2 className={`h-3.5 w-3.5 ${muted ? tone : 'text-indigo-600 dark:text-indigo-400'}`} />;
        case 'battery':
            return <Battery className={`h-3.5 w-3.5 ${muted ? tone : 'text-emerald-600 dark:text-emerald-400'}`} />;
        case 'race':
            return <FileText className={`h-3.5 w-3.5 ${muted ? tone : 'text-blue-600 dark:text-blue-400'}`} />;
        case 'debug':
            return <FileText className={`h-3.5 w-3.5 ${muted ? tone : 'text-amber-600 dark:text-amber-400'}`} />;
        case 'heard':
            return <Radio className={`h-3.5 w-3.5 ${muted ? tone : 'text-cyan-600 dark:text-cyan-400'}`} />;
        case 'reason':
            return <ShieldAlert className={`h-3.5 w-3.5 ${muted ? tone : 'text-rose-600 dark:text-rose-400'}`} />;
        case 'provisioning':
            return <ShieldCheck className={`h-3.5 w-3.5 ${muted ? tone : 'text-emerald-700 dark:text-emerald-400'}`} />;
        default:
            return <Activity className={`h-3.5 w-3.5 ${muted ? tone : 'text-gray-500 dark:text-slate-400'}`} />;
    }
}

export function DeviceStatPills({ items }: { items: DeviceStatPill[] }): React.JSX.Element {
    return (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
            {items.map((item) => (
                <div
                    key={item.label}
                    title={item.title}
                    className={[
                        'rounded-lg border px-3 py-2 shadow-sm',
                        item.muted
                            ? 'border-gray-100 bg-gray-50/90 dark:border-slate-800 dark:bg-slate-900/40'
                            : 'border-gray-200 bg-white transition-all hover:-translate-y-[1px] hover:shadow-md dark:border-slate-700 dark:bg-slate-900',
                    ].join(' ')}
                >
                    <div
                        className={[
                            'flex items-center gap-1.5 text-[11px]',
                            item.muted
                                ? 'text-gray-400 dark:text-slate-600'
                                : 'text-gray-500 dark:text-slate-400',
                        ].join(' ')}
                    >
                        {iconFor(item.icon, !!item.muted)}
                        <span>{item.label}</span>
                    </div>
                    <div
                        className={[
                            'mt-1 truncate text-sm font-semibold',
                            item.muted
                                ? 'text-gray-400 dark:text-slate-600'
                                : 'text-gray-900 dark:text-slate-100',
                        ].join(' ')}
                    >
                        {item.value || '-'}
                    </div>
                </div>
            ))}
        </div>
    );
}
